import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileExcelOutlined, FilePdfOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Alert,
  Button,
  DatePicker,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import dayjs from 'dayjs';
import { API } from '@/services/api.service';
import { errorText, formatAmount, formatRate, saveBlob } from '../format';

const { Text } = Typography;

// RSD always; one second currency next to it, EUR unless the client wants another.
const CURRENCIES = ['EUR', 'USD', 'RUB', 'GBP', 'CHF'];

const amount = (value) =>
  value == null || Math.abs(value) < 0.005 ? '' : formatAmount(value);
const percent = (value) =>
  value == null
    ? '—'
    : `${(value * 100).toLocaleString('sr-RS', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}%`;

/**
 * The monthly result of one company as the client will get it: the official
 * income statement (APR form by the company's size) for the month and the
 * year to date, in RSD and a second currency, with the markups and
 * EBIT/EBITDA. The accountant checks it here; it reaches the client only as a
 * file of a published pack (Pack settings → "Mesečni rezultat").
 *
 * @param {Object} props - Component props
 * @param {string} props.companyId - Company whose ledger is used
 * @returns {JSX.Element} The panel
 */
export default function MonthlyResultPanel({ companyId }) {
  const { t } = useTranslation('clientReports');
  // The report goes out after the month closes, so last month is the default.
  const [month0, setMonth0] = useState(
    dayjs().subtract(1, 'month').startOf('month')
  );
  const [language, setLanguage] = useState('sr');
  const [currency, setCurrency] = useState('EUR');
  const [belowOperating, setBelowOperating] = useState(true);
  // As a new pack item starts: short, no markup.
  const [variant, setVariant] = useState('short');
  const [markup, setMarkup] = useState('none');
  const year = month0.year();
  const month = month0.month() + 1;
  const params = {
    year,
    month,
    language,
    currency,
    belowOperating,
    variant,
    markup,
  };

  const { data, isLoading, error } = useQuery({
    queryKey: ['client-reports-monthly-result', companyId, params],
    queryFn: () => API.clientReports.getMonthlyResult(companyId, params),
    retry: false,
  });
  const report = data?.data;
  const fx = report?.fx;

  const exportMutation = useMutation({
    mutationFn: (format) =>
      API.clientReports.exportMonthlyResult(companyId, { ...params, format }),
    onSuccess: saveBlob,
    onError: async (e) => message.error(await errorText(e)),
  });

  const errorMessage =
    error?.response?.status === 404
      ? t('fccs.noLedger', { year })
      : error?.response?.data?.message || error?.message;

  const columns = [
    {
      title: t('monthlyResult.accounts'),
      dataIndex: 'accounts',
      key: 'accounts',
      width: 130,
    },
    {
      title: t('monthlyResult.item'),
      dataIndex: 'label',
      key: 'label',
      render: (label, row) => (
        <span
          style={{
            paddingLeft: row.level * 12,
            fontWeight: row.level === 0 ? 600 : undefined,
          }}
        >
          {label}
        </span>
      ),
    },
    { title: 'AOP', dataIndex: 'aop', key: 'aop', width: 70, align: 'center' },
    {
      title: `${t('monthlyResult.month')} RSD`,
      dataIndex: 'month',
      key: 'month',
      align: 'right',
      width: 150,
      render: amount,
    },
    {
      title: `${t('monthlyResult.ytd')} RSD`,
      dataIndex: 'ytd',
      key: 'ytd',
      align: 'right',
      width: 150,
      render: amount,
    },
    ...(fx
      ? [
          {
            title: `${t('monthlyResult.month')} ${fx.currency}`,
            dataIndex: 'monthFx',
            key: 'monthFx',
            align: 'right',
            width: 140,
            render: amount,
          },
          {
            title: `${t('monthlyResult.ytd')} ${fx.currency}`,
            dataIndex: 'ytdFx',
            key: 'ytdFx',
            align: 'right',
            width: 140,
            render: amount,
          },
        ]
      : []),
  ];

  // The lines the client gets (by variant and markup), as the server prints
  // them in the file — in the report's language.
  const indicatorRows = report?.indicatorRows || [];
  const isShort = report?.variant === 'short';

  const indicatorValue = (kind) => (value) =>
    kind === 'ratio' ? percent(value) : formatAmount(value);

  const indicatorColumns = [
    {
      title: t('monthlyResult.indicatorsTitle'),
      dataIndex: 'label',
      key: 'label',
    },
    ...['month', 'ytd'].map((period) => ({
      title: `${t(`monthlyResult.${period}`)} RSD`,
      key: period,
      align: 'right',
      width: 150,
      render: (_, row) => indicatorValue(row.kind)(row[period]),
    })),
    ...(fx
      ? ['month', 'ytd'].map((period) => ({
          title: `${t(`monthlyResult.${period}`)} ${fx.currency}`,
          key: `${period}Fx`,
          align: 'right',
          width: 140,
          render: (_, row) =>
            row.kind === 'ratio' ? '' : formatAmount(row[`${period}Fx`]),
        }))
      : []),
  ];

  return (
    <div>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message={t('monthlyResult.hint')}
      />
      <Space wrap style={{ display: 'flex', marginBottom: 12 }}>
        <DatePicker
          picker="month"
          allowClear={false}
          value={month0}
          format="MM.YYYY"
          onChange={(value) => value && setMonth0(value.startOf('month'))}
        />
        <Select
          style={{ width: 130 }}
          value={language}
          onChange={setLanguage}
          options={[
            { value: 'sr', label: t('pack.languageSr') },
            { value: 'en', label: t('pack.languageEn') },
          ]}
        />
        <Text>{t('monthlyResult.secondCurrency')}</Text>
        <Select
          style={{ width: 120 }}
          value={currency}
          onChange={setCurrency}
          options={[
            { value: 'RSD', label: t('monthlyResult.rsdOnly') },
            ...CURRENCIES.map((c) => ({ value: c, label: c })),
          ]}
        />
        <Select
          style={{ width: 250 }}
          value={variant}
          onChange={setVariant}
          options={['short', 'full'].map((v) => ({
            value: v,
            label: t(`monthlyShort.variants.${v}`),
          }))}
        />
        <Select
          style={{ width: 250 }}
          value={markup}
          onChange={setMarkup}
          options={['none', 'mu1', 'mu2', 'both'].map((m) => ({
            value: m,
            label: t(`monthlyShort.markups.${m}`),
          }))}
        />
        {variant === 'full' && (
          <Space size={4}>
            <Switch
              size="small"
              checked={belowOperating}
              onChange={setBelowOperating}
            />
            <Text>{t('monthlyResult.belowOperating')}</Text>
          </Space>
        )}
        <Button
          shape="round"
          icon={<FileExcelOutlined />}
          disabled={!report}
          loading={
            exportMutation.isPending && exportMutation.variables === 'xlsx'
          }
          onClick={() => exportMutation.mutate('xlsx')}
        >
          Excel
        </Button>
        <Button
          shape="round"
          icon={<FilePdfOutlined />}
          disabled={!report}
          loading={
            exportMutation.isPending && exportMutation.variables === 'pdf'
          }
          onClick={() => exportMutation.mutate('pdf')}
        >
          PDF
        </Button>
      </Space>

      {errorMessage && (
        <Alert
          type="warning"
          showIcon
          message={errorMessage}
          style={{ marginBottom: 12 }}
        />
      )}

      {report && (
        <Space wrap size={6} style={{ marginBottom: 8 }}>
          <Tag>
            {t(
              report.form === 'MIKRO'
                ? 'monthlyResult.formMikro'
                : 'monthlyResult.formFull'
            )}{' '}
            · {report.companySize}
          </Tag>
          {fx && (
            <Tag color="blue">
              {t('monthlyResult.rate', {
                currency: fx.currency,
                rate: formatRate(fx.rate),
                date: dayjs(fx.date).format('DD.MM.YYYY'),
              })}
            </Tag>
          )}
        </Space>
      )}

      {!isShort && (
        <Table
          scroll={{ x: 'max-content' }}
          rowKey="aop"
          size="small"
          loading={isLoading}
          pagination={false}
          columns={columns}
          dataSource={report?.rows || []}
        />
      )}

      {report && (
        <>
          <Table
            style={{ marginTop: isShort ? 0 : 16 }}
            scroll={{ x: 'max-content' }}
            rowKey="key"
            size="small"
            pagination={false}
            columns={indicatorColumns}
            dataSource={indicatorRows}
          />
          <ul style={{ marginTop: 12, paddingLeft: 18, fontSize: 12 }}>
            {report.notes.map((note) => (
              <li key={note} style={{ opacity: 0.8 }}>
                {note}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
